import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/i18n/LangContext";
import { SignaturePad } from "@/components/editor/SignaturePad";
import { AddPdfDialog } from "@/components/editor/AddPdfDialog";
import { CropPdfDialog } from "@/components/editor/CropPdfDialog";
import { Rnd } from "react-rnd";
import { EditorSideAd } from "@/components/ads/EditorSideAd";
import { ProgressBar, stageLabel } from "@/components/ProgressBar";
import { InfoSections } from "@/components/InfoSections";
import { GUIDES } from "@/content/seoContent";
import {
  fileToUint8, loadPdfjsDoc, renderPage, exportEditedPdf, downloadBytes, uid, FONT_OPTIONS, fontCss, removeColorFromDataUrl, rotateDataUrl,
} from "@/lib/pdfEditor";
import {
  ArrowLeft, UploadCloud, Type, Image as ImageIcon, PenLine, Plus, Download,
  ChevronLeft, ChevronRight, X, Loader2, Trash2, FilePlus2, RotateCw, RotateCcw, Scissors,
  Bold, Italic, Underline, Paintbrush, Undo2, PaintBucket,
} from "lucide-react";

const T = {
  it: { title: "Modifica PDF", sub: "Aggiungi testo, immagini e firme, riordina o unisci pagine, poi scarica il nuovo PDF. Tutto nel tuo browser.", drop: "Trascina qui il tuo PDF", dropHint: "PDF fino a 50MB • Elaborazione locale nel browser", back: "Torna alla home", addText: "Testo", addImage: "Immagine", addSign: "Firma", addPdf: "Aggiungi PDF", download: "Scarica PDF", pages: "Pagine", page: "Pagina", noSel: "Nessuna pagina selezionata", loading: "Caricamento PDF...", exporting: "Generazione PDF...", fontSize: "Dimensione", color: "Colore", delete: "Elimina", moveLeft: "Sposta a sinistra", moveRight: "Sposta a destra", rotate: "Ruota 90°", rotateAll: "Ruota tutte", newText: "Testo", exported: "PDF scaricato!", exportErr: "Errore durante la generazione del PDF", loadErr: "Impossibile aprire il PDF", empty: "Elimina tutte le pagine? Deve restare almeno una pagina.", sigTitle: "Disegna la tua firma", sigClear: "Cancella", sigAdd: "Aggiungi firma", sigColor: "Colore", mergeTitle: "Aggiungi pagine da un altro PDF", selectAll: "Seleziona tutte", deselectAll: "Deseleziona tutte", insertAt: "Inserisci:", atStart: "All'inizio", atEnd: "Alla fine", afterPage: "Dopo pagina", insert: "Inserisci", font: "Font", brush: "Pennello", brushColor: "Colore pennello", brushWidth: "Spessore", undo: "Annulla", bold: "Grassetto", italic: "Corsivo", underline: "Sottolineato", dblClickEdit: "Doppio click per modificare il testo", cropTool: "Ritaglia PDF", cropTitle: "Ritaglia da altro PDF", cropLoad: "Carica il PDF da cui ritagliare", cropPage: "Pagina", cropZoom: "Zoom", cropInsert: "Inserisci ritaglio", cropInsertFull: "Pagina intera", cropChangeFile: "Cambia file", cropErr: "Impossibile leggere il PDF", bucket: "Rimuovi Colore", tolerance: "Tolleranza", bucketHint: "Clicca un colore su un'immagine per renderlo trasparente", bucketWorking: "Lavorazione immagine in corso...", bucketDone: "Colore rimosso!", bucketErr: "Errore durante la rimozione del colore", imgRotateCw: "Ruota +90°", imgRotateCcw: "Ruota -90°", cropShape: "Forma", shapeSquare: "Quadrato", shapeCircle: "Cerchio" },
  en: { title: "Edit PDF", sub: "Add text, images and signatures, reorder or merge pages, then download the new PDF. All in your browser.", drop: "Drag your PDF here", dropHint: "PDF up to 50MB • Processed locally in your browser", back: "Back to home", addText: "Text", addImage: "Image", addSign: "Signature", addPdf: "Add PDF", download: "Download PDF", pages: "Pages", page: "Page", noSel: "No page selected", loading: "Loading PDF...", exporting: "Generating PDF...", fontSize: "Size", color: "Color", delete: "Delete", moveLeft: "Move left", moveRight: "Move right", rotate: "Rotate 90°", rotateAll: "Rotate all", newText: "Text", exported: "PDF downloaded!", exportErr: "Error while generating the PDF", loadErr: "Could not open the PDF", empty: "Delete all pages? At least one page must remain.", sigTitle: "Draw your signature", sigClear: "Clear", sigAdd: "Add signature", sigColor: "Color", mergeTitle: "Add pages from another PDF", selectAll: "Select all", deselectAll: "Deselect all", insertAt: "Insert:", atStart: "At start", atEnd: "At end", afterPage: "After page", insert: "Insert", font: "Font", brush: "Brush", brushColor: "Brush color", brushWidth: "Thickness", undo: "Undo", bold: "Bold", italic: "Italic", underline: "Underline", dblClickEdit: "Double-click to edit the text", cropTool: "Crop PDF", cropTitle: "Crop from another PDF", cropLoad: "Load the PDF to crop from", cropPage: "Page", cropZoom: "Zoom", cropInsert: "Insert crop", cropInsertFull: "Full page", cropChangeFile: "Change file", cropErr: "Could not read the PDF", bucket: "Remove Color", tolerance: "Tolerance", bucketHint: "Click a color on an image to make it transparent", bucketWorking: "Processing image...", bucketDone: "Color removed!", bucketErr: "Error while removing the color", imgRotateCw: "Rotate +90°", imgRotateCcw: "Rotate -90°", cropShape: "Shape", shapeSquare: "Square", shapeCircle: "Circle" },
  es: { title: "Editar PDF", sub: "Añade texto, imágenes y firmas, reordena o une páginas y descarga el nuevo PDF. Todo en tu navegador.", drop: "Arrastra aquí tu PDF", dropHint: "PDF hasta 50MB • Procesado localmente en el navegador", back: "Volver al inicio", addText: "Texto", addImage: "Imagen", addSign: "Firma", addPdf: "Añadir PDF", download: "Descargar PDF", pages: "Páginas", page: "Página", noSel: "Ninguna página seleccionada", loading: "Cargando PDF...", exporting: "Generando PDF...", fontSize: "Tamaño", color: "Color", delete: "Eliminar", moveLeft: "Mover a la izquierda", moveRight: "Mover a la derecha", rotate: "Girar 90°", rotateAll: "Girar todas", newText: "Texto", exported: "¡PDF descargado!", exportErr: "Error al generar el PDF", loadErr: "No se pudo abrir el PDF", empty: "¿Eliminar todas las páginas? Debe quedar al menos una.", sigTitle: "Dibuja tu firma", sigClear: "Borrar", sigAdd: "Añadir firma", sigColor: "Color", mergeTitle: "Añadir páginas de otro PDF", selectAll: "Seleccionar todas", deselectAll: "Deseleccionar todas", insertAt: "Insertar:", atStart: "Al inicio", atEnd: "Al final", afterPage: "Después de la página", insert: "Insertar", font: "Fuente", brush: "Pincel", brushColor: "Color del pincel", brushWidth: "Grosor", undo: "Deshacer", bold: "Negrita", italic: "Cursiva", underline: "Subrayado", dblClickEdit: "Doble clic para editar el texto", cropTool: "Recortar PDF", cropTitle: "Recortar de otro PDF", cropLoad: "Carga el PDF a recortar", cropPage: "Página", cropZoom: "Zoom", cropInsert: "Insertar recorte", cropInsertFull: "Página completa", cropChangeFile: "Cambiar archivo", cropErr: "No se pudo leer el PDF", bucket: "Quitar color", tolerance: "Tolerancia", bucketHint: "Haz clic en un color de una imagen para hacerlo transparente", bucketWorking: "Procesando imagen...", bucketDone: "¡Color eliminado!", bucketErr: "Error al eliminar el color", imgRotateCw: "Girar +90°", imgRotateCcw: "Girar -90°", cropShape: "Forma", shapeSquare: "Cuadrado", shapeCircle: "Círculo" },
  fr: { title: "Modifier le PDF", sub: "Ajoutez texte, images et signatures, réorganisez ou fusionnez les pages, puis téléchargez le nouveau PDF. Tout dans votre navigateur.", drop: "Glissez votre PDF ici", dropHint: "PDF jusqu'à 50 Mo • Traité localement dans le navigateur", back: "Retour à l'accueil", addText: "Texte", addImage: "Image", addSign: "Signature", addPdf: "Ajouter un PDF", download: "Télécharger le PDF", pages: "Pages", page: "Page", noSel: "Aucune page sélectionnée", loading: "Chargement du PDF...", exporting: "Génération du PDF...", fontSize: "Taille", color: "Couleur", delete: "Supprimer", moveLeft: "Déplacer à gauche", moveRight: "Déplacer à droite", rotate: "Pivoter 90°", rotateAll: "Tout pivoter", newText: "Texte", exported: "PDF téléchargé !", exportErr: "Erreur lors de la génération du PDF", loadErr: "Impossible d'ouvrir le PDF", empty: "Supprimer toutes les pages ? Au moins une page doit rester.", sigTitle: "Dessinez votre signature", sigClear: "Effacer", sigAdd: "Ajouter la signature", sigColor: "Couleur", mergeTitle: "Ajouter des pages d'un autre PDF", selectAll: "Tout sélectionner", deselectAll: "Tout désélectionner", insertAt: "Insérer :", atStart: "Au début", atEnd: "À la fin", afterPage: "Après la page", insert: "Insérer", font: "Police", brush: "Pinceau", brushColor: "Couleur du pinceau", brushWidth: "Épaisseur", undo: "Annuler", bold: "Gras", italic: "Italique", underline: "Souligné", dblClickEdit: "Double-cliquez pour modifier le texte", cropTool: "Rogner PDF", cropTitle: "Rogner depuis un autre PDF", cropLoad: "Chargez le PDF à rogner", cropPage: "Page", cropZoom: "Zoom", cropInsert: "Insérer le rognage", cropInsertFull: "Page entière", cropChangeFile: "Changer de fichier", cropErr: "Impossible de lire le PDF", bucket: "Supprimer couleur", tolerance: "Tolérance", bucketHint: "Cliquez sur une couleur d'une image pour la rendre transparente", bucketWorking: "Traitement de l'image...", bucketDone: "Couleur supprimée !", bucketErr: "Erreur lors de la suppression de la couleur", imgRotateCw: "Pivoter +90°", imgRotateCcw: "Pivoter -90°", cropShape: "Forme", shapeSquare: "Carré", shapeCircle: "Cercle" },
  de: { title: "PDF bearbeiten", sub: "Text, Bilder und Unterschriften hinzufügen, Seiten neu anordnen oder zusammenführen und das neue PDF herunterladen. Alles im Browser.", drop: "PDF hierher ziehen", dropHint: "PDF bis 50 MB • Lokal im Browser verarbeitet", back: "Zurück zur Startseite", addText: "Text", addImage: "Bild", addSign: "Unterschrift", addPdf: "PDF hinzufügen", download: "PDF herunterladen", pages: "Seiten", page: "Seite", noSel: "Keine Seite ausgewählt", loading: "PDF wird geladen...", exporting: "PDF wird erstellt...", fontSize: "Größe", color: "Farbe", delete: "Löschen", moveLeft: "Nach links", moveRight: "Nach rechts", rotate: "90° drehen", rotateAll: "Alle drehen", newText: "Text", exported: "PDF heruntergeladen!", exportErr: "Fehler beim Erstellen des PDF", loadErr: "PDF konnte nicht geöffnet werden", empty: "Alle Seiten löschen? Mindestens eine Seite muss bleiben.", sigTitle: "Zeichnen Sie Ihre Unterschrift", sigClear: "Löschen", sigAdd: "Unterschrift hinzufügen", sigColor: "Farbe", mergeTitle: "Seiten aus einem anderen PDF hinzufügen", selectAll: "Alle auswählen", deselectAll: "Alle abwählen", insertAt: "Einfügen:", atStart: "Am Anfang", atEnd: "Am Ende", afterPage: "Nach Seite", insert: "Einfügen", font: "Schrift", brush: "Pinsel", brushColor: "Pinselfarbe", brushWidth: "Stärke", undo: "Rückgängig", bold: "Fett", italic: "Kursiv", underline: "Unterstrichen", dblClickEdit: "Doppelklick zum Bearbeiten des Textes", cropTool: "PDF zuschneiden", cropTitle: "Aus anderem PDF zuschneiden", cropLoad: "PDF zum Zuschneiden laden", cropPage: "Seite", cropZoom: "Zoom", cropInsert: "Ausschnitt einfügen", cropInsertFull: "Ganze Seite", cropChangeFile: "Datei wechseln", cropErr: "PDF konnte nicht gelesen werden", bucket: "Farbe entfernen", tolerance: "Toleranz", bucketHint: "Klicken Sie auf eine Farbe in einem Bild, um sie transparent zu machen", bucketWorking: "Bild wird verarbeitet...", bucketDone: "Farbe entfernt!", bucketErr: "Fehler beim Entfernen der Farbe", imgRotateCw: "+90° drehen", imgRotateCcw: "-90° drehen", cropShape: "Form", shapeSquare: "Quadrat", shapeCircle: "Kreis" },
};

export default function PdfEditor() {
  const { lang, t: tGlobal } = useLang();
  const t = T[lang] || T.it;

  const sourcesRef = useRef({}); // sourceId -> { bytes, doc }
  const [pages, setPages] = useState([]);
  const [annotations, setAnnotations] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [mainImg, setMainImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selItem, setSelItem] = useState(null);
  const [wrapPx, setWrapPx] = useState({ w: 1, h: 1 });

  const [sigOpen, setSigOpen] = useState(false);
  const [addPdf, setAddPdf] = useState({ open: false, newPages: [], sourceId: null });

  // brush / whiteout
  const [brushes, setBrushes] = useState({}); // pageId -> [{ id, color, wFrac, pts:[{x,y}] }]
  const [brushMode, setBrushMode] = useState(false);
  const [brushColor, setBrushColor] = useState("#ffffff");
  const [brushWidth, setBrushWidth] = useState(0.012); // fraction of page height
  const brushCanvasRef = useRef(null);
  const brushDrawing = useRef(null);
  const brushCursorRef = useRef(null);
  const [brushHover, setBrushHover] = useState(false);
  const [exportPct, setExportPct] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);

  // magic bucket (remove color)
  const [bucketMode, setBucketMode] = useState(false);
  const [tolerance, setTolerance] = useState(30);
  const [bucketBusy, setBucketBusy] = useState(false);

  // universal undo history (snapshots of { annotations, brushes })
  const historyRef = useRef([]);
  const lastPushRef = useRef(0);
  const [histLen, setHistLen] = useState(0);
  const annRef = useRef(annotations);
  const brRef = useRef(brushes);
  annRef.current = annotations;
  brRef.current = brushes;

  const pushHistory = () => {
    const now = Date.now();
    if (now - lastPushRef.current < 400) return;
    lastPushRef.current = now;
    const h = historyRef.current;
    h.push({ annotations: annRef.current, brushes: brRef.current });
    if (h.length > 30) h.shift();
    setHistLen(h.length);
  };

  const undoLast = () => {
    const h = historyRef.current;
    const snap = h.pop();
    if (!snap) return;
    setAnnotations(snap.annotations);
    setBrushes(snap.brushes);
    setSelItem(null);
    setEditingId(null);
    setHistLen(h.length);
  };

  const updateItemH = (id, patch) => { pushHistory(); updateItem(id, patch); };

  const wrapRef = useRef(null);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const addPdfInputRef = useRef(null);

  const measure = useCallback(() => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      setWrapPx({ w: r.width, h: r.height });
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const registerSource = async (bytes) => {
    const id = uid("src");
    const doc = await loadPdfjsDoc(bytes);
    sourcesRef.current[id] = { bytes, doc };
    return { id, doc };
  };

  const buildPages = async (sourceId, doc, indices) => {
    const out = [];
    for (const idx of indices) {
      const r = await renderPage(doc, idx, 0.4);
      out.push({ id: uid("pg"), sourceId, pageIndex: idx, thumb: r.dataUrl, wPt: r.wPt, hPt: r.hPt, rotate: 0 });
    }
    return out;
  };

  const handleFirstUpload = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const bytes = await fileToUint8(file);
      const { id, doc } = await registerSource(bytes);
      const indices = Array.from({ length: doc.numPages }, (_, i) => i);
      const pgs = await buildPages(id, doc, indices);
      setPages(pgs);
      setAnnotations({});
      setBrushes({});
      historyRef.current = [];
      setHistLen(0);
      await selectPage(pgs[0]);
    } catch (e) {
      console.error(e);
      toast.error(t.loadErr);
    } finally {
      setLoading(false);
    }
  };

  const selectPage = async (pg) => {
    if (!pg) { setSelectedId(null); setMainImg(null); return; }
    setSelectedId(pg.id);
    setSelItem(null);
    const doc = sourcesRef.current[pg.sourceId].doc;
    const r = await renderPage(doc, pg.pageIndex, 1.6, pg.rotate || 0);
    setMainImg(r.dataUrl);
    requestAnimationFrame(measure);
  };

  // ---- overlays ----
  const curItems = selectedId ? (annotations[selectedId] || []) : [];
  const setItems = (updater) =>
    setAnnotations((prev) => ({ ...prev, [selectedId]: updater(prev[selectedId] || []) }));
  const updateItem = (id, patch) => setItems((its) => its.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const deleteItem = (id) => { pushHistory(); setItems((its) => its.filter((i) => i.id !== id)); setSelItem(null); };

  // selected page visual point-height (for point<->screen px mapping)
  const curPage = pages.find((p) => p.id === selectedId);
  const vhPt = curPage ? ((curPage.rotate === 90 || curPage.rotate === 270) ? curPage.wPt : curPage.hPt) : 1;
  const fontPxOf = (pt) => (Math.max(1, pt) * wrapPx.h) / (vhPt || 1);

  // ---- brush / whiteout ----
  const curStrokes = selectedId ? (brushes[selectedId] || []) : [];
  const redrawBrush = useCallback(() => {
    const cv = brushCanvasRef.current;
    if (!cv) return;
    if (cv.width !== Math.round(wrapPx.w)) cv.width = Math.round(wrapPx.w);
    if (cv.height !== Math.round(wrapPx.h)) cv.height = Math.round(wrapPx.h);
    const ctx = cv.getContext("2d", { willReadFrequently: true });
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const strokes = selectedId ? (brushes[selectedId] || []) : [];
    for (const s of strokes) drawStrokeOn(ctx, s, cv.width, cv.height);
  }, [brushes, selectedId, wrapPx]);

  const drawStrokeOn = (ctx, s, W, H) => {
    const pts = s.pts || [];
    if (pts.length === 0) return;
    ctx.strokeStyle = s.color;
    ctx.lineWidth = Math.max(1, s.wFrac * H);
    ctx.beginPath();
    ctx.moveTo(pts[0].x * W, pts[0].y * H);
    if (pts.length === 1) ctx.lineTo(pts[0].x * W + 0.01, pts[0].y * H + 0.01);
    else for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x * W, pts[i].y * H);
    ctx.stroke();
  };

  useEffect(() => { redrawBrush(); }, [redrawBrush, mainImg]);

  const brushPoint = (e) => {
    const cv = brushCanvasRef.current;
    const r = cv.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const onBrushDown = (e) => {
    if (!brushMode || !selectedId) return;
    e.preventDefault();
    e.stopPropagation();
    const p = brushPoint(e);
    brushDrawing.current = { id: uid("br"), color: brushColor, wFrac: brushWidth, pts: [p] };
    const cv = brushCanvasRef.current;
    const ctx = cv.getContext("2d");
    drawStrokeOn(ctx, brushDrawing.current, cv.width, cv.height);
    window.addEventListener("mousemove", onBrushMove);
    window.addEventListener("mouseup", onBrushUp);
  };
  const onBrushMove = (e) => {
    const st = brushDrawing.current;
    if (!st) return;
    const p = brushPoint(e);
    const cv = brushCanvasRef.current;
    const ctx = cv.getContext("2d");
    const prev = st.pts[st.pts.length - 1];
    st.pts.push(p);
    ctx.strokeStyle = st.color;
    ctx.lineWidth = Math.max(1, st.wFrac * cv.height);
    ctx.beginPath();
    ctx.moveTo(prev.x * cv.width, prev.y * cv.height);
    ctx.lineTo(p.x * cv.width, p.y * cv.height);
    ctx.stroke();
  };
  const onBrushUp = () => {
    const st = brushDrawing.current;
    brushDrawing.current = null;
    window.removeEventListener("mousemove", onBrushMove);
    window.removeEventListener("mouseup", onBrushUp);
    if (!st) return;
    pushHistory();
    setBrushes((prev) => ({ ...prev, [selectedId]: [...(prev[selectedId] || []), st] }));
  };
  const onBrushHoverMove = (e) => {
    const el = brushCursorRef.current;
    const cv = brushCanvasRef.current;
    if (!el || !cv) return;
    const r = cv.getBoundingClientRect();
    el.style.left = `${e.clientX - r.left}px`;
    el.style.top = `${e.clientY - r.top}px`;
  };

  const undoBrush = () => {
    if (!selectedId) return;
    setBrushes((prev) => {
      const arr = prev[selectedId] || [];
      if (arr.length === 0) return prev;
      return { ...prev, [selectedId]: arr.slice(0, -1) };
    });
  };

  const rotateImage = async (it, clockwise) => {
    try {
      const newUrl = await rotateDataUrl(it.dataUrl, clockwise);
      const W = wrapPx.w, H = wrapPx.h;
      const wPx = it.wFrac * W, hPx = it.hFrac * H;
      const cx = it.xFrac * W + wPx / 2, cy = it.yFrac * H + hPx / 2;
      const nW = hPx, nH = wPx;
      const nx = Math.min(Math.max(0, cx - nW / 2), Math.max(0, W - nW));
      const ny = Math.min(Math.max(0, cy - nH / 2), Math.max(0, H - nH));
      pushHistory();
      updateItem(it.id, { dataUrl: newUrl, wFrac: nW / W, hFrac: nH / H, xFrac: nx / W, yFrac: ny / H });
    } catch (err) {
      console.error(err);
    }
  };

  const applyBucket = async (it, e) => {
    if (bucketBusy) return;
    const r = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    setBucketBusy(true);
    const tid = toast.loading(t.bucketWorking);
    try {
      await new Promise((res) => setTimeout(res, 30));
      const newUrl = await removeColorFromDataUrl(it.dataUrl, fx, fy, tolerance);
      pushHistory();
      updateItem(it.id, { dataUrl: newUrl });
      toast.success(t.bucketDone);
    } catch (err) {
      console.error(err);
      toast.error(t.bucketErr);
    } finally {
      toast.dismiss(tid);
      setBucketBusy(false);
    }
  };

  const addText = () => {
    if (!selectedId) return;
    pushHistory();
    const id = uid("txt");
    setItems((its) => [...its, { id, type: "text", xFrac: 0.15, yFrac: 0.12, fontPt: 16, color: "#111111", text: t.newText, bold: false, italic: false, underline: false, fontFamily: "sans" }]);
    setSelItem(id);
  };
  const addImageItem = (dataUrl, isSig = false) => {
    if (!selectedId) return;
    const probe = new Image();
    probe.onload = () => {
      const id = uid(isSig ? "sig" : "img");
      const pw = wrapPx.w || 800;
      const ph = wrapPx.h || 1100;
      // Box aspect = natural image aspect, so screen preview and exported PDF match 1:1
      let wFrac = isSig ? 0.35 : 0.3;
      let hFrac = (wFrac * pw * (probe.height / probe.width)) / ph;
      if (hFrac > 0.6) {
        wFrac *= 0.6 / hFrac;
        hFrac = 0.6;
      }
      pushHistory();
      setItems((its) => [...its, { id, type: "image", xFrac: 0.2, yFrac: 0.2, wFrac, hFrac, dataUrl }]);
      setSelItem(id);
    };
    probe.src = dataUrl;
  };
  const onImageFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => addImageItem(reader.result, false);
    reader.readAsDataURL(file);
  };

  // drag / resize handled by react-rnd on the overlays (px ↔ frac via the single wrapPx scale factor)

  // ---- page ops ----
  const movePage = (idx, dir) => {
    setPages((ps) => {
      const j = idx + dir;
      if (j < 0 || j >= ps.length) return ps;
      const n = [...ps];
      [n[idx], n[j]] = [n[j], n[idx]];
      return n;
    });
  };
  const deletePage = (id) => {
    setPages((ps) => {
      if (ps.length <= 1) { toast.error(t.empty); return ps; }
      const n = ps.filter((p) => p.id !== id);
      if (selectedId === id) selectPage(n[0]);
      return n;
    });
    setAnnotations((a) => { const c = { ...a }; delete c[id]; return c; });
    setBrushes((b) => { const c = { ...b }; delete c[id]; return c; });
  };

  const rotatePage = async (id) => {
    const pg = pages.find((p) => p.id === id);
    if (!pg) return;
    const nr = ((pg.rotate || 0) + 90) % 360;
    const doc = sourcesRef.current[pg.sourceId].doc;
    const thumb = (await renderPage(doc, pg.pageIndex, 0.4, nr)).dataUrl;
    setPages((ps) => ps.map((p) => (p.id === id ? { ...p, rotate: nr, thumb } : p)));
    if (selectedId === id) {
      const r = await renderPage(doc, pg.pageIndex, 1.6, nr);
      setMainImg(r.dataUrl);
    }
  };

  const rotateAll = async () => {
    setLoading(true);
    try {
      const updated = await Promise.all(pages.map(async (pg) => {
        const nr = ((pg.rotate || 0) + 90) % 360;
        const doc = sourcesRef.current[pg.sourceId].doc;
        const thumb = (await renderPage(doc, pg.pageIndex, 0.4, nr)).dataUrl;
        return { ...pg, rotate: nr, thumb };
      }));
      setPages(updated);
      const sel = updated.find((p) => p.id === selectedId);
      if (sel) {
        const r = await renderPage(sourcesRef.current[sel.sourceId].doc, sel.pageIndex, 1.6, sel.rotate);
        setMainImg(r.dataUrl);
      }
    } finally {
      setLoading(false);
    }
  };

  const openAddPdf = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const bytes = await fileToUint8(file);
      const { id, doc } = await registerSource(bytes);
      const previews = [];
      for (let i = 0; i < doc.numPages; i++) {
        const r = await renderPage(doc, i, 0.4);
        previews.push({ id: uid("np"), pageIndex: i, thumb: r.dataUrl });
      }
      setAddPdf({ open: true, newPages: previews, sourceId: id });
    } catch (e) {
      toast.error(t.loadErr);
    } finally {
      setLoading(false);
    }
  };
  const confirmAddPdf = async (indices, position) => {
    const sid = addPdf.sourceId;
    const doc = sourcesRef.current[sid].doc;
    const newPgs = await buildPages(sid, doc, indices);
    setPages((ps) => {
      const n = [...ps];
      n.splice(position, 0, ...newPgs);
      return n;
    });
    setAddPdf({ open: false, newPages: [], sourceId: null });
    toast.success(`${newPgs.length} ${t.pages.toLowerCase()} +`);
  };

  const doExport = async () => {
    if (pages.length === 0) return;
    setExporting(true);
    setExportPct(2);
    try {
      const bytes = await exportEditedPdf({
        pages, sources: sourcesRef.current, annotations, brushes,
        onProgress: (done, total) => setExportPct(Math.round((done / total) * 95)),
      });
      setExportPct(100);
      downloadBytes(bytes, "dotec-modificato.pdf");
      toast.success(t.exported);
    } catch (e) {
      console.error(e);
      toast.error(t.exportErr);
    } finally {
      setExporting(false);
    }
  };

  const hasDoc = pages.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 lg:px-8" onMouseDown={() => setSelItem(null)}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <Link to="/" data-testid="back-home-link" className="group inline-flex items-center gap-2 rounded-full border-2 border-emerald-500 bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700 shadow-sm transition-all hover:-translate-y-0.5 hover:bg-emerald-500 hover:text-white">
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" /> {t.back}
        </Link>
        {hasDoc && (
          <button onClick={doExport} disabled={exporting} data-testid="editor-download" className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-60">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? t.exporting : t.download}
          </button>
        )}
      </div>

      {!hasDoc ? (
        <div className="text-center">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t.title}</h1>
          <p className="mx-auto mt-3 max-w-2xl text-slate-500">{t.sub}</p>
          <div className="mx-auto mt-8 max-w-xl">
            <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" data-testid="editor-file-input" onChange={(e) => handleFirstUpload(e.target.files?.[0])} />
            <button onClick={() => fileInputRef.current?.click()} disabled={loading} data-testid="editor-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (!f) return;
                if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error(t.loadErr); return; }
                handleFirstUpload(f);
              }}
              className={`flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-16 transition-colors disabled:opacity-60 ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-emerald-400 hover:bg-emerald-50/40"}`}>
              {loading ? <Loader2 className="h-10 w-10 animate-spin text-emerald-600" /> : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><UploadCloud className="h-8 w-8" /></div>}
              <p className="mt-4 font-display text-xl font-bold text-slate-900">{loading ? t.loading : t.drop}</p>
              <p className="mt-1 text-sm text-slate-400">{t.dropHint}</p>
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-6">
          <div className="min-w-0 flex-1">
          {exporting && (
            <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
              <ProgressBar progress={exportPct} label={stageLabel(tGlobal, "generate")} />
            </div>
          )}
          {/* Toolbar */}
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            <button onClick={undoLast} disabled={histLen === 0} title={t.undo} data-testid="tool-undo" className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-40"><Undo2 className="h-4 w-4" /> {t.undo}</button>
            <div className="mx-1 h-6 w-px bg-slate-200" />
            <button onClick={addText} data-testid="tool-add-text" className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"><Type className="h-4 w-4" /> {t.addText}</button>
            <input ref={imgInputRef} type="file" accept="image/*" className="hidden" data-testid="editor-image-input" onChange={(e) => { onImageFile(e.target.files?.[0]); e.target.value = ""; }} />
            <button onClick={() => imgInputRef.current?.click()} data-testid="tool-add-image" className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"><ImageIcon className="h-4 w-4" /> {t.addImage}</button>
            <button onClick={() => setSigOpen(true)} data-testid="tool-add-signature" className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"><PenLine className="h-4 w-4" /> {t.addSign}</button>
            <div className="mx-1 h-6 w-px bg-slate-200" />
            <input ref={addPdfInputRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { openAddPdf(e.target.files?.[0]); e.target.value = ""; }} />
            <button onClick={() => addPdfInputRef.current?.click()} data-testid="tool-add-pdf" className="flex items-center gap-2 rounded-lg bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-200"><FilePlus2 className="h-4 w-4" /> {t.addPdf}</button>
            <button onClick={() => setCropOpen(true)} data-testid="tool-crop-pdf" className="flex items-center gap-2 rounded-lg bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-200"><Scissors className="h-4 w-4" /> {t.cropTool}</button>
            <button onClick={rotateAll} data-testid="tool-rotate-all" className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"><RotateCw className="h-4 w-4" /> {t.rotateAll}</button>
            <div className="mx-1 h-6 w-px bg-slate-200" />
            <button onClick={() => { setBrushMode((v) => !v); setBucketMode(false); setSelItem(null); }} data-testid="tool-brush" className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${brushMode ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}><Paintbrush className="h-4 w-4" /> {t.brush}</button>
            <button onClick={() => { setBucketMode((v) => !v); setBrushMode(false); setSelItem(null); }} data-testid="tool-bucket" className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${bucketMode ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}><PaintBucket className="h-4 w-4" /> {t.bucket}</button>

            {bucketMode && (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5" onMouseDown={(e) => e.stopPropagation()}>
                <span className="text-xs font-semibold text-emerald-700">{t.tolerance}</span>
                <input type="range" min="1" max="100" step="1" value={tolerance} onChange={(e) => setTolerance(Number(e.target.value))} title={t.tolerance} data-testid="bucket-tolerance" className="w-24" />
                <span className="w-7 text-xs font-bold text-emerald-700" data-testid="bucket-tolerance-value">{tolerance}</span>
                <span className="hidden text-xs text-emerald-600 lg:inline">{bucketBusy ? t.bucketWorking : t.bucketHint}</span>
              </div>
            )}

            {brushMode && (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-1.5" onMouseDown={(e) => e.stopPropagation()}>
                <span className="text-xs font-semibold text-emerald-700">{t.brushColor}</span>
                <input type="color" value={brushColor} onChange={(e) => setBrushColor(e.target.value)} title={t.brushColor} data-testid="brush-color" className="h-7 w-8 cursor-pointer rounded border border-slate-200" />
                <span className="ml-1 text-xs font-semibold text-emerald-700">{t.brushWidth}</span>
                <input type="range" min="0.004" max="0.06" step="0.002" value={brushWidth} onChange={(e) => setBrushWidth(Number(e.target.value))} title={t.brushWidth} data-testid="brush-width" className="w-24" />
                <button onClick={undoBrush} disabled={curStrokes.length === 0} data-testid="brush-undo" className="flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-emerald-700 shadow-sm hover:bg-emerald-100 disabled:opacity-40"><Undo2 className="h-3.5 w-3.5" /> {t.undo}</button>
              </div>
            )}

            {!brushMode && !bucketMode && selItem && (() => {
              const item = curItems.find((i) => i.id === selItem);
              if (!item) return null;
              return (
                <div className="ml-auto flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5" onMouseDown={(e) => e.stopPropagation()}>
                  {item.type === "text" && (
                    <>
                      <select value={item.fontFamily || "sans"} onChange={(e) => updateItemH(item.id, { fontFamily: e.target.value })} title={t.font} data-testid="text-font-family" className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 outline-none">
                        {FONT_OPTIONS.map((f) => (<option key={f.key} value={f.key}>{f.label}</option>))}
                      </select>
                      <div className="flex items-center gap-0.5 rounded-md border border-slate-200 bg-white p-0.5">
                        <button onClick={() => updateItemH(item.id, { bold: !item.bold })} title={t.bold} data-testid="text-bold" className={`flex h-6 w-6 items-center justify-center rounded ${item.bold ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Bold className="h-3.5 w-3.5" /></button>
                        <button onClick={() => updateItemH(item.id, { italic: !item.italic })} title={t.italic} data-testid="text-italic" className={`flex h-6 w-6 items-center justify-center rounded ${item.italic ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Italic className="h-3.5 w-3.5" /></button>
                        <button onClick={() => updateItemH(item.id, { underline: !item.underline })} title={t.underline} data-testid="text-underline" className={`flex h-6 w-6 items-center justify-center rounded ${item.underline ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}><Underline className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-slate-500">{t.fontSize}</span>
                        <input type="number" min="1" max="200" step="0.1" value={item.fontPt} onChange={(e) => { const v = Math.min(200, Math.max(1, Number(e.target.value) || 1)); updateItemH(item.id, { fontPt: v }); }} data-testid="text-size-input" className="w-16 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-700 outline-none" />
                      </div>
                      <input type="color" value={item.color} onChange={(e) => updateItemH(item.id, { color: e.target.value })} title={t.color} data-testid="text-color" className="h-7 w-8 cursor-pointer rounded border border-slate-200" />
                    </>
                  )}
                  <button onClick={() => deleteItem(item.id)} data-testid="delete-item" className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100"><Trash2 className="h-3.5 w-3.5" /> {t.delete}</button>
                </div>
              );
            })()}
          </div>

          {/* Main page area */}
          <div className="rounded-2xl border border-slate-200 bg-slate-100 p-4 shadow-sm">
            {mainImg ? (
              <div className="mx-auto w-full max-w-3xl">
                <div ref={wrapRef} className={`relative mx-auto select-none bg-white shadow-md ${brushMode ? "cursor-crosshair" : ""}`} onMouseDown={(e) => e.stopPropagation()}>
                  <img src={mainImg} alt="page" className="block w-full" draggable={false} onLoad={measure} />
                  <canvas
                    ref={brushCanvasRef}
                    data-testid="brush-canvas"
                    onMouseDown={onBrushDown}
                    onMouseMove={onBrushHoverMove}
                    onMouseEnter={() => setBrushHover(true)}
                    onMouseLeave={() => setBrushHover(false)}
                    className="absolute inset-0 z-30 h-full w-full"
                    style={{ pointerEvents: brushMode ? "auto" : "none", touchAction: "none", cursor: brushMode ? "none" : undefined }}
                  />
                  {brushMode && (
                    <div
                      ref={brushCursorRef}
                      data-testid="brush-cursor-preview"
                      className="pointer-events-none absolute z-40 rounded-full"
                      style={{
                        width: `${Math.max(3, brushWidth * wrapPx.h)}px`,
                        height: `${Math.max(3, brushWidth * wrapPx.h)}px`,
                        transform: "translate(-50%, -50%)",
                        border: "1px solid rgba(51, 65, 85, 0.65)",
                        backgroundColor: `${brushColor}26`,
                        display: brushHover ? "block" : "none",
                        transition: "width 80ms ease, height 80ms ease",
                      }}
                    />
                  )}
                  {wrapPx.w > 0 && curItems.map((it) => {
                    const sel = selItem === it.id;
                    const dot = sel
                      ? { width: 12, height: 12, background: "#059669", border: "2px solid #fff", borderRadius: 9999 }
                      : {};
                    const common = {
                      bounds: "parent",
                      disableDragging: brushMode || bucketMode || editingId === it.id,
                      onDragStart: () => setSelItem(it.id),
                      onDragStop: (e, d) => {
                        pushHistory();
                        updateItem(it.id, { xFrac: d.x / wrapPx.w, yFrac: d.y / wrapPx.h });
                      },
                      style: { zIndex: sel ? 20 : 15, pointerEvents: brushMode ? "none" : "auto" },
                    };
                    if (it.type === "text") {
                      const fpx = fontPxOf(it.fontPt);
                      const editing = editingId === it.id;
                      return (
                        <Rnd
                          key={it.id}
                          {...common}
                          position={{ x: it.xFrac * wrapPx.w, y: it.yFrac * wrapPx.h }}
                          size={{ width: "auto", height: "auto" }}
                          enableResizing={false}
                        >
                          <div
                            data-testid="text-overlay-box"
                            title={t.dblClickEdit}
                            onMouseDown={() => setSelItem(it.id)}
                            onDoubleClick={() => { pushHistory(); setEditingId(it.id); }}
                            className={`${editing ? "cursor-text" : "cursor-move"} ${sel ? "ring-2 ring-emerald-500" : ""}`}
                          >
                            <input
                              value={it.text}
                              readOnly={!editing}
                              onChange={(e) => updateItem(it.id, { text: e.target.value })}
                              onBlur={() => setEditingId(null)}
                              onMouseDown={(e) => editing && e.stopPropagation()}
                              ref={(el) => { if (editing && el && document.activeElement !== el) el.focus(); }}
                              data-testid="text-overlay-input"
                              style={{
                                fontSize: `${fpx}px`,
                                lineHeight: 1.1,
                                color: it.color,
                                fontFamily: fontCss(it.fontFamily),
                                fontWeight: it.bold ? 700 : 400,
                                fontStyle: it.italic ? "italic" : "normal",
                                textDecoration: it.underline ? "underline" : "none",
                                width: `${Math.max(60, (it.text.length + 2) * fpx * 0.6)}px`,
                                pointerEvents: editing ? "auto" : "none",
                                cursor: editing ? "text" : "move",
                              }}
                              className="border border-dashed border-emerald-400/60 bg-white/40 px-1 outline-none focus:bg-white/80"
                            />
                          </div>
                        </Rnd>
                      );
                    }
                    return (
                      <Rnd
                        key={it.id}
                        {...common}
                        position={{ x: it.xFrac * wrapPx.w, y: it.yFrac * wrapPx.h }}
                        size={{ width: it.wFrac * wrapPx.w, height: it.hFrac * wrapPx.h }}
                        lockAspectRatio
                        enableResizing={{ topLeft: true, topRight: true, bottomLeft: true, bottomRight: true }}
                        onResizeStart={() => setSelItem(it.id)}
                        onResizeStop={(e, dir, ref, delta, pos) => {
                          pushHistory();
                          updateItem(it.id, {
                            wFrac: ref.offsetWidth / wrapPx.w,
                            hFrac: ref.offsetHeight / wrapPx.h,
                            xFrac: pos.x / wrapPx.w,
                            yFrac: pos.y / wrapPx.h,
                          });
                        }}
                        resizeHandleStyles={{ topLeft: dot, topRight: dot, bottomLeft: dot, bottomRight: dot }}
                      >
                        <div
                          data-testid="image-overlay-box"
                          onMouseDown={(e) => {
                            if (bucketMode) { e.stopPropagation(); applyBucket(it, e); return; }
                            setSelItem(it.id);
                          }}
                          className={`relative h-full w-full ${bucketMode ? "cursor-crosshair" : "cursor-move"} ${sel && !bucketMode ? "ring-2 ring-emerald-500" : ""}`}
                        >
                          {sel && !bucketMode && (
                            <div className="absolute -top-9 left-1/2 z-30 flex -translate-x-1/2 gap-1.5" onMouseDown={(e) => e.stopPropagation()}>
                              <button onClick={() => rotateImage(it, false)} title={t.imgRotateCcw} data-testid="image-rotate-ccw" className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white shadow hover:bg-emerald-700"><RotateCcw className="h-4 w-4" /></button>
                              <button onClick={() => rotateImage(it, true)} title={t.imgRotateCw} data-testid="image-rotate-cw" className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white shadow hover:bg-emerald-700"><RotateCw className="h-4 w-4" /></button>
                            </div>
                          )}
                          <img
                            src={it.dataUrl}
                            alt="overlay"
                            draggable={false}
                            className="h-full w-full select-none"
                            style={{ objectFit: "fill" }}
                          />
                        </div>
                      </Rnd>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            )}
          </div>

          {/* Thumbnail bar */}
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold text-slate-600">{t.pages} ({pages.length})</p>
            <div className="flex gap-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3" data-testid="thumbnail-bar">
              {pages.map((pg, idx) => (
                <div key={pg.id} className={`relative flex-shrink-0 rounded-lg border-2 p-1 transition-all ${selectedId === pg.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`thumb-${idx}`}>
                  <button onClick={() => deletePage(pg.id)} data-testid={`thumb-delete-${idx}`} className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600"><X className="h-3 w-3" /></button>
                  <button onClick={() => rotatePage(pg.id)} title={t.rotate} data-testid={`thumb-rotate-${idx}`} className="absolute -left-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white shadow hover:bg-emerald-700"><RotateCw className="h-3 w-3" /></button>
                  <button onClick={() => selectPage(pg)} className="block">
                    <img src={pg.thumb} alt={`p${idx + 1}`} className="h-32 w-24 object-contain" />
                  </button>
                  <div className="mt-1 flex items-center justify-between">
                    <button onClick={() => movePage(idx, -1)} disabled={idx === 0} title={t.moveLeft} data-testid={`thumb-left-${idx}`} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                    <span className="text-xs font-bold text-slate-500">{idx + 1}</span>
                    <button onClick={() => movePage(idx, 1)} disabled={idx === pages.length - 1} title={t.moveRight} data-testid={`thumb-right-${idx}`} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>

          <EditorSideAd />
        </div>
      )}

      <SignaturePad open={sigOpen} onClose={() => setSigOpen(false)} onSave={(d) => addImageItem(d, true)} labels={{ title: t.sigTitle, clear: t.sigClear, add: t.sigAdd, color: t.sigColor }} />
      <AddPdfDialog open={addPdf.open} onClose={() => setAddPdf({ open: false, newPages: [], sourceId: null })} onConfirm={confirmAddPdf} newPages={addPdf.newPages} totalCurrent={pages.length} labels={{ title: t.mergeTitle, selectAll: t.selectAll, deselectAll: t.deselectAll, insertAt: t.insertAt, atStart: t.atStart, atEnd: t.atEnd, afterPage: t.afterPage, insert: t.insert }} />

      <CropPdfDialog
        open={cropOpen}
        onClose={() => setCropOpen(false)}
        onInsert={(dataUrl) => addImageItem(dataUrl)}
        labels={{ title: t.cropTitle, load: t.cropLoad, page: t.cropPage, zoom: t.cropZoom, insert: t.cropInsert, insertFull: t.cropInsertFull, changeFile: t.cropChangeFile, err: t.cropErr, shapeSquare: t.shapeSquare, shapeCircle: t.shapeCircle }}
      />

      {/* Tool guide (SEO / AdSense long-form content) */}
      {!hasDoc && (
        <div className="mt-8 rounded-3xl border border-slate-100 bg-white/60">
          <InfoSections sections={GUIDES.edit[lang] || GUIDES.edit.en} testId="editor-guide" />
        </div>
      )}
    </div>
  );
}
